// core/modals/expenses/AddExpenseCategory.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import { X } from 'feather-icons-react/build/IconComponents';
import { expenseCategoryActions } from '../../action/expenseCategoryActions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const AddExpenseCategory = ({ onSuccess, onClose, isOpen }) => {
    // Form state
    const [formData, setFormData] = useState({
        category_name: '',
        description: '',
        color: '#6c757d',
        icon: 'Folder',
        status: 'active'
    });
    
    const [loading, setLoading] = useState(false);
    
    // Get icon and color options
    const iconOptions = expenseCategoryActions.getIconOptions();
    const colorOptions = expenseCategoryActions.getColorOptions();
    
    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    // Handle select change
    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option?.value || '' }));
    };
    
    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.category_name.trim()) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Category name is required',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }
        
        setLoading(true);
        
        const result = await expenseCategoryActions.createCategory(formData);
        
        setLoading(false);
        
        if (result.success) {
            onSuccess();
        }
    };
    
    return (
        <Modal show={isOpen} onHide={onClose} size="md" centered backdrop="static">
            <Modal.Header className="border-0 pb-0">
                <Modal.Title>
                    <h5 className="mb-0">Add New Category</h5>
                </Modal.Title>
                <button type="button" className="btn-close" onClick={onClose} disabled={loading}>
                    <X size={18} />
                </button>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <Form.Label>Category Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            name="category_name"
                            value={formData.category_name}
                            onChange={handleInputChange}
                            placeholder="Enter category name"
                            required
                            disabled={loading}
                        />
                    </div>
                    
                    <div className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows="2"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter description (optional)"
                            disabled={loading}
                        />
                    </div>
                    
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Label>Icon</Form.Label>
                            <Select
                                options={iconOptions}
                                value={iconOptions.find(opt => opt.value === formData.icon)}
                                onChange={(opt) => handleSelectChange('icon', opt)}
                                placeholder="Select icon"
                                isDisabled={loading}
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <Form.Label>Color</Form.Label>
                            <Select
                                options={colorOptions}
                                value={colorOptions.find(opt => opt.value === formData.color)}
                                onChange={(opt) => handleSelectChange('color', opt)}
                                placeholder="Select color"
                                isDisabled={loading}
                                formatOptionLabel={({ label, color }) => (
                                    <div className="d-flex align-items-center">
                                        <span 
                                            className="me-2" 
                                            style={{ 
                                                display: 'inline-block',
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: color,
                                                borderRadius: '4px'
                                            }}
                                        ></span>
                                        <span>{label}</span>
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                    
                    <div className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                label="Active"
                                name="status"
                                value="active"
                                checked={formData.status === 'active'}
                                onChange={handleInputChange}
                                disabled={loading}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="Inactive"
                                name="status"
                                value="inactive"
                                checked={formData.status === 'inactive'}
                                onChange={handleInputChange}
                                disabled={loading}
                            />
                        </div>
                    </div>
                    
                    {/* Preview */}
                    <div className="mb-3 p-3 bg-light rounded">
                        <Form.Label className="mb-2">Preview</Form.Label>
                        <div className="d-flex align-items-center">
                            <div 
                                className="rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                style={{ backgroundColor: formData.color + '20', width: '40px', height: '40px' }}
                            >
                                <span style={{ color: formData.color }}>
                                    {iconOptions.find(opt => opt.value === formData.icon)?.label.split(' ')[0] || '📁'}
                                </span>
                            </div>
                            <div>
                                <strong>{formData.category_name || 'Category Name'}</strong>
                                <br />
                                <span className={`badge ${formData.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                    {formData.status?.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : 'Save Category'}
                        </button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

AddExpenseCategory.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired
};

export default AddExpenseCategory;